import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { getModule } from '@jwp/ott-common/src/modules/container';
import EntitlementController from '@jwp/ott-common/src/controllers/EntitlementController';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';

export default function usePlayableMedia(
  item: PlaylistItem,
  {
    streamingProtocol,
    shouldKeepPreviousData = false,
    trackHistory = true,
  }: { streamingProtocol?: 'dash' | 'hls'; shouldKeepPreviousData?: boolean; trackHistory?: boolean } = {},
) {
  const entitlementController = getModule(EntitlementController);
  const { i18n } = useTranslation();
  const { language } = i18n;
  const { subscription, passes, user } = useAccountStore(({ subscription, passes, user }) => ({ subscription, passes, user }));

  useEffect(() => {
    return () => {
      void entitlementController.stopPlaybackSession(item);
    };
  }, [entitlementController, item]);

  const {
    data: { playableItem, startOffset = 0 } = {},
    isLoading,
    error,
  } = useQuery({
    staleTime: 0,
    gcTime: 0,
    queryKey: [
      'media-signed',
      item.mediaid,
      language,
      streamingProtocol,
      !!subscription,
      passes.map(({ offerId }) => offerId),
      item.mediaOffers?.map(({ id }) => id),
      user?.id,
    ],
    queryFn: () => entitlementController.startPlaybackSession(item, language, streamingProtocol, trackHistory),
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    retry: entitlementController.playbackRetryCount,
    retryDelay: 1000,
  });

  return {
    playableItem,
    startOffset,
    isLoading,
    error,
  };
}
